import {
  Container,
  Eyebrow,
  SectionLabel,
  AccentButton,
  CircleCTA,
  SurfaceCard,
  Pill,
  DiamondStat,
} from "@/components/shared/Primitives";
import { RadarRings, HexOutline } from "@/components/shared/Decorations";
import { openCalendly } from "@/components/shared/BookCallButton";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/Reveal";
import { WORK_CASES, HOME_STATS, tint } from "@/data/site";

const MockPanel = ({ from, to, tall = false }) => (
  <div className={`relative ${tall ? "h-56" : "h-36"} overflow-hidden rounded-[12px]`} style={{ background: `linear-gradient(135deg, ${from}26, ${to}10)`, border: "1px solid var(--stroke-0)" }}>
    <div className="absolute inset-0 bg-grid opacity-40" />
    <div className="absolute left-4 top-4 flex gap-1.5">
      {[from, to, from].map((c, i) => <span key={i} className="h-2 w-2 rounded-full" style={{ background: c, opacity: 0.75 }} />)}
    </div>
    <div className="absolute bottom-4 left-4 right-4 flex items-end gap-1.5">
      {[34, 56, 44, 72, 52, 88, 64, 48].map((h, i) => (
        <span key={i} className="flex-1 rounded-sm" style={{ height: `${tall ? h * 1.6 : h}px`, background: `linear-gradient(${to}, ${from})`, opacity: 0.55 }} />
      ))}
    </div>
  </div>
);

export default function Work() {
  const featured = WORK_CASES[0];
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -right-44 -top-36 h-[600px] w-[600px] opacity-60" style={{ color: "var(--page-accent)" }}>
          <RadarRings className="h-full w-full" />
        </div>
        <Container className="relative z-10 pb-16 pt-16 text-center lg:pt-24">
          <Reveal className="flex justify-center"><Eyebrow>Selected Work</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display mx-auto mt-7" style={{ fontSize: "clamp(54px, 9vw, 138px)", lineHeight: 0.9, letterSpacing: "0.02em", color: "var(--text-0)" }}>
              PROOF, NOT<br /><span className="glow-accent" style={{ color: "var(--page-accent)" }}>PROMISES.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mx-auto mt-7 max-w-xl text-[15px] font-light leading-relaxed" style={{ color: "var(--text-2)" }}>
              A look at the brands we've scaled — and the numbers that actually moved. Real programs,
              real revenue, no vanity metrics.
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-12 flex flex-wrap justify-center gap-x-12 gap-y-6">
              {HOME_STATS.map((s) => (
                <div key={s.label} className="text-center">
                  <p className="font-display tabular text-4xl leading-none" style={{ color: "var(--text-0)" }}>{s.value}</p>
                  <p className="font-tech mt-1.5 text-[10px] uppercase tracking-[0.12em]" style={{ color: "var(--text-3)" }}>{s.label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </Container>
      </section>

      {/* GRID */}
      <section className="py-14 lg:py-20">
        <Container>
          <SectionLabel index="01" total="02">Case studies</SectionLabel>
          <Stagger className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {WORK_CASES.map((c) => (
              <StaggerItem key={c.brand}>
                <SurfaceCard className="flex h-full flex-col">
                  <MockPanel from={c.accentFrom} to={c.accentTo} />
                  <div className="mt-5 flex items-center justify-between">
                    <h3 className="text-lg font-semibold" style={{ color: "var(--text-0)" }}>{c.brand}</h3>
                    <span className="font-tech text-[10px] uppercase tracking-[0.12em]" style={{ color: "var(--text-3)" }}>{c.category}</span>
                  </div>
                  <div className="mt-4">
                    <p className="font-display text-3xl" style={{ color: c.accentFrom }}>{c.metric}</p>
                    <p className="font-tech text-[10px] uppercase tracking-[0.12em]" style={{ color: "var(--text-3)" }}>{c.metricLabel}</p>
                  </div>
                  <p className="mt-4 flex-1 text-[13px] font-light leading-relaxed" style={{ color: "var(--text-2)" }}>{c.blurb}</p>
                  <div className="mt-5 flex flex-wrap gap-2">{c.services.map((s) => <Pill key={s}>{s}</Pill>)}</div>
                </SurfaceCard>
              </StaggerItem>
            ))}
          </Stagger>
        </Container>
      </section>

      {/* FEATURED */}
      <section className="py-16 lg:py-24">
        <Container>
          <SectionLabel index="02" total="02">Featured</SectionLabel>
          <Reveal>
            <div className="grid items-center gap-10 rounded-[24px] p-7 sm:p-10 lg:grid-cols-2" style={{ background: tint(6), border: `1px solid ${tint(22)}` }}>
              <div>
                <div className="flex items-center gap-3">
                  <span className="font-display text-2xl" style={{ color: "var(--page-accent)" }}>{featured.brand}</span>
                  <span className="font-tech text-[10px] uppercase tracking-[0.12em]" style={{ color: "var(--text-3)" }}>{featured.category}</span>
                </div>
                <h3 className="mt-4 text-2xl font-semibold leading-tight sm:text-3xl" style={{ color: "var(--text-0)" }}>
                  From a 12% afterthought to 44% of revenue — in four months.
                </h3>
                <div className="mt-6 space-y-4">
                  {[["Challenge", "A large, engaged list doing almost nothing. Flows were broken, sends were sporadic, and email barely registered in the P&L."], ["Approach", "A 90-day rebuild: deliverability fixes, a full flow suite, segmentation and a tested campaign calendar."], ["Result", "Email became the single most profitable channel, tripling owned revenue without spending a cent more on ads."]].map(([k, v]) => (
                    <div key={k}>
                      <p className="font-tech text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--page-accent)" }}>{k}</p>
                      <p className="mt-1.5 text-[14px] font-light leading-relaxed" style={{ color: "var(--text-2)" }}>{v}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-8"><AccentButton to="/contact">Get results like this</AccentButton></div>
              </div>
              <div className="relative">
                <div className="pointer-events-none absolute -right-6 -top-8 h-32 w-32 opacity-60" style={{ color: "var(--page-accent)" }}><HexOutline className="h-full w-full" /></div>
                <MockPanel from={featured.accentFrom} to={featured.accentTo} tall />
                <div className="mt-5 flex justify-center gap-8">
                  <DiamondStat value="44%" label="EMAIL SHARE" />
                  <DiamondStat value="3.1x" label="REVENUE" warm />
                </div>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden py-24">
        <Container className="relative z-10 grid items-end gap-12 lg:grid-cols-[1fr_auto]">
          <div>
            <Eyebrow>Your brand next</Eyebrow>
            <h2 className="font-display mt-6" style={{ fontSize: "clamp(42px, 6vw, 90px)", lineHeight: 0.95, letterSpacing: "0.02em", color: "var(--text-0)" }}>
              LET'S WRITE YOUR<br />CASE STUDY.
            </h2>
          </div>
          <CircleCTA to="/contact" sub="30 min · no pitch" />
        </Container>
      </section>
    </div>
  );
}
